import { useState, useEffect, useCallback } from 'react';
import { api } from '@/api/client';
import { useAuthStore } from '@/state/authStore';

interface User {
  id: string; email: string; name: string; cognito_sub: string | null;
  cognito_status?: string | null;
  last_login_at?: string | null;
  is_feedbacker: boolean; is_admin: boolean;
  can_access_learning_review_app: boolean; is_learning_review_veteran: boolean;
  default_reviewer_id: string | null; default_reviewer_name: string | null;
}
interface Feedbacker { id: string; name: string; email: string }

function normalizeEmail(value: string) {
  return value.replace(/[\u200B-\u200D\uFEFF]/g, '').trim().toLowerCase();
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function RoleBadge({ user }: { user: User }) {
  if (user.is_admin) return <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">管理者</span>;
  if (user.is_feedbacker) return <span className="rounded bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">フィードバッカー</span>;
  return <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">コーチ</span>;
}

function formatLastLogin(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

function LastLoginCell({ user }: { user: User }) {
  const formatted = formatLastLogin(user.last_login_at);
  if (!formatted) {
    if (!user.cognito_sub) {
      return <span className="text-xs text-orange-500">招待未完了</span>;
    }
    if (user.cognito_status === 'FORCE_CHANGE_PASSWORD') {
      return <span className="text-xs text-orange-500">未ログイン</span>;
    }
    if (user.cognito_status === 'CONFIRMED') {
      return <span className="text-xs text-gray-400">記録なし</span>;
    }
    return <span className="text-xs text-gray-400">確認不可</span>;
  }
  return <span className="text-xs text-gray-700">{formatted}</span>;
}

function canConfigureLearningAccess(role: 'coach' | 'feedbacker' | 'admin') {
  return role === 'feedbacker' || role === 'admin';
}

export function CoachManagementPage() {
  const currentUser = useAuthStore(state => state.currentUser);
  const [users, setUsers] = useState<User[]>([]);
  const [feedbackers, setFeedbackers] = useState<Feedbacker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState<'coach' | 'feedbacker' | 'admin'>('coach');
  const [inviteDefaultReviewer, setInviteDefaultReviewer] = useState('');
  const [inviteCanAccessLearningReview, setInviteCanAccessLearningReview] = useState(false);
  const [inviteIsLearningReviewVeteran, setInviteIsLearningReviewVeteran] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editReviewerId, setEditReviewerId] = useState('');
  const [editingNameUserId, setEditingNameUserId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editingRoleUserId, setEditingRoleUserId] = useState<string | null>(null);
  const [editRole, setEditRole] = useState<'coach' | 'feedbacker' | 'admin'>('coach');
  const [editRoleDefaultReviewerId, setEditRoleDefaultReviewerId] = useState('');
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersResp, fbResp] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/feedbackers'),
      ]);
      setUsers(usersResp.data);
      setFeedbackers(fbResp.data);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'エラー');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const errorMessage = (e: unknown, fallback: string) => {
    const data = (e as {
      response?: {
        data?: {
          detail?: { message?: string; code?: string } | string | Array<{ loc?: unknown[]; msg?: string }>;
          message?: string;
        };
      };
      userMessage?: string;
    }).response?.data;
    const detail = data?.detail;
    if (Array.isArray(detail)) {
      const emailError = detail.find((item) => item.loc?.includes('email'));
      if (emailError) return 'メールアドレスを正しい形式で入力してください。';
    }
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
      if (detail.message) return detail.message;
      if (detail.code) return detail.code;
    }
    return data?.message || (e as { userMessage?: string }).userMessage || fallback;
  };

  const handleInvite = async () => {
    const email = normalizeEmail(inviteEmail);
    const name = inviteName.trim();
    if (!email || !name) {
      setInviteError('メールアドレスと名前を入力してください。');
      return;
    }
    if (!isValidEmail(email)) {
      setInviteError('メールアドレスを正しい形式で入力してください。');
      return;
    }
    if (inviteRole !== 'admin' && !inviteDefaultReviewer) {
      setInviteError('デフォルトフィードバッカーを選択してください。');
      return;
    }
    setInviting(true);
    setInviteError(null);
    try {
      await api.post('/admin/users', {
        email,
        name,
        is_feedbacker: inviteRole === 'feedbacker' || inviteRole === 'admin',
        is_admin: inviteRole === 'admin',
        can_access_learning_review_app: canConfigureLearningAccess(inviteRole) ? inviteCanAccessLearningReview : false,
        is_learning_review_veteran: inviteIsLearningReviewVeteran,
        default_reviewer_id: inviteRole === 'admin' ? null : inviteDefaultReviewer,
      });
      setShowInvite(false);
      setInviteEmail(''); setInviteName(''); setInviteRole('coach' as 'coach' | 'feedbacker' | 'admin'); setInviteDefaultReviewer(''); setInviteCanAccessLearningReview(false); setInviteIsLearningReviewVeteran(false); setInviteError(null);
      await fetchData();
    } catch (e: unknown) {
      setInviteError(errorMessage(e, '招待に失敗しました'));
    } finally {
      setInviting(false);
    }
  };

  const handleChangeReviewer = async (userId: string) => {
    try {
      await api.patch(`/admin/users/${userId}`, { default_reviewer_id: editReviewerId });
      setEditingUserId(null);
      setEditReviewerId('');
      await fetchData();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      alert(detail || '変更に失敗しました');
    }
  };

  const roleValue = (user: User): 'coach' | 'feedbacker' | 'admin' => {
    if (user.is_admin) return 'admin';
    if (user.is_feedbacker) return 'feedbacker';
    return 'coach';
  };

  const handleInviteRoleChange = (role: 'coach' | 'feedbacker' | 'admin') => {
    setInviteRole(role);
    setInviteError(null);
    if (!canConfigureLearningAccess(role)) {
      setInviteCanAccessLearningReview(false);
    }
  };

  const handleChangeRole = async (userId: string) => {
    try {
      const payload: {
        is_feedbacker: boolean;
        is_admin: boolean;
        default_reviewer_id?: string;
        can_access_learning_review_app?: boolean;
      } = {
        is_feedbacker: editRole === 'feedbacker' || editRole === 'admin',
        is_admin: editRole === 'admin',
      };
      if (editRole === 'coach') {
        payload.can_access_learning_review_app = false;
      }
      if (editRole !== 'admin') {
        if (!editRoleDefaultReviewerId) {
          alert('デフォルトフィードバッカーを選択してください');
          return;
        }
        payload.default_reviewer_id = editRoleDefaultReviewerId;
      }
      await api.patch(`/admin/users/${userId}`, {
        ...payload,
      });
      setEditingRoleUserId(null);
      setEditRoleDefaultReviewerId('');
      await fetchData();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: { message?: string } | string; message?: string } } }).response?.data;
      const message = typeof detail?.detail === 'string' ? detail.detail : detail?.detail?.message ?? detail?.message;
      alert(message || 'ロール変更に失敗しました');
    }
  };

  const handleChangeLearningFlags = async (
    user: User,
    patch: { can_access_learning_review_app?: boolean; is_learning_review_veteran?: boolean },
  ) => {
    if ('can_access_learning_review_app' in patch && patch.can_access_learning_review_app) {
      if (!user.is_admin && !user.is_feedbacker) {
        alert('学習レビューアプリ利用は管理者またはフィードバッカーにのみ設定できます');
        return;
      }
    }
    try {
      await api.patch(`/admin/users/${user.id}`, patch);
      await fetchData();
    } catch (e: unknown) {
      alert(errorMessage(e, '学習レビュー設定の変更に失敗しました'));
    }
  };

  const handleChangeName = async (userId: string) => {
    const name = editName.trim();
    if (!name) return;
    try {
      await api.patch(`/admin/users/${userId}`, { name });
      setEditingNameUserId(null);
      setEditName('');
      await fetchData();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: { message?: string } | string } } }).response?.data?.detail;
      alert(typeof detail === 'string' ? detail : detail?.message ?? '名前の変更に失敗しました');
    }
  };

  const handleDeleteUser = async (user: User) => {
    if (user.id === currentUser?.id) return;
    const confirmed = window.confirm(`ユーザー「${user.name}」を削除します。よろしいですか？`);
    if (!confirmed) return;
    setDeletingUserId(user.id);
    try {
      await api.delete(`/admin/users/${user.id}`);
      await fetchData();
    } catch (e: unknown) {
      alert(errorMessage(e, '削除に失敗しました'));
    } finally {
      setDeletingUserId(null);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">読み込み中...</div>;
  if (error) return <div className="p-8 text-center text-red-500">{error}</div>;

  return (
    <div>
      <h1 className="text-xl font-semibold text-gray-900">コーチ管理</h1>
      <p className="mt-1 text-sm text-gray-500">ユーザーの追加・編集</p>

      <div className="mt-6">
        <div className="flex items-center justify-between">
          <h2 className="font-medium text-gray-700">ユーザー一覧</h2>
          <button type="button" onClick={() => { setInviteError(null); setShowInvite(true); }} className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700">+ ユーザー追加</button>
        </div>

        {showInvite && (
          <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <h3 className="text-sm font-medium text-blue-900">新規ユーザー追加</h3>
            <div className="mt-2 space-y-3">
              <div>
                <label htmlFor="invite-email" className="block text-xs text-gray-600 mb-1">メールアドレス</label>
                <input
                  id="invite-email"
                  type="email"
                  placeholder="example@sugoi-kaigi.com"
                  value={inviteEmail}
                  onChange={e => { setInviteEmail(e.target.value); setInviteError(null); }}
                  aria-invalid={Boolean(inviteError && !isValidEmail(normalizeEmail(inviteEmail)))}
                  className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                />
              </div>
              <div>
                <label htmlFor="invite-name" className="block text-xs text-gray-600 mb-1">名前</label>
                <input id="invite-name" type="text" placeholder="名前" value={inviteName} onChange={e => { setInviteName(e.target.value); setInviteError(null); }} className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm" />
              </div>
              <div>
                <label htmlFor="invite-role" className="block text-xs text-gray-600 mb-1">ロール</label>
                <select id="invite-role" value={inviteRole} onChange={e => handleInviteRoleChange(e.target.value as 'coach' | 'feedbacker' | 'admin')}
                  className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm">
                  <option value="coach">コーチ</option>
                  <option value="feedbacker">フィードバッカー</option>
                  <option value="admin">管理者</option>
                </select>
              </div>
              {(inviteRole === 'coach' || inviteRole === 'feedbacker') && (
                <div>
                  <label htmlFor="invite-default-reviewer" className="block text-xs text-gray-600 mb-1">デフォルトフィードバッカー</label>
                  <select id="invite-default-reviewer" value={inviteDefaultReviewer} onChange={e => { setInviteDefaultReviewer(e.target.value); setInviteError(null); }} className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm">
                    <option value="">選択してください</option>
                    {feedbackers.map(fb => <option key={fb.id} value={fb.id}>{fb.name}</option>)}
                  </select>
                </div>
              )}
              <div className="grid gap-2 sm:grid-cols-2">
                <label className={`flex items-start gap-2 rounded border px-3 py-2 text-sm ${canConfigureLearningAccess(inviteRole) ? 'border-gray-200 bg-white text-gray-700' : 'border-gray-100 bg-gray-50 text-gray-400'}`}>
                  <input
                    type="checkbox"
                    checked={canConfigureLearningAccess(inviteRole) && inviteCanAccessLearningReview}
                    disabled={!canConfigureLearningAccess(inviteRole)}
                    onChange={e => setInviteCanAccessLearningReview(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    アプリ利用
                    {inviteRole === 'admin' && <span className="block text-xs text-gray-500">管理者は常に利用可</span>}
                  </span>
                </label>
                <label className="flex items-start gap-2 rounded border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={inviteIsLearningReviewVeteran}
                    onChange={e => setInviteIsLearningReviewVeteran(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>ベテラン判定対象</span>
                </label>
              </div>
              {inviteError && (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {inviteError}
                </div>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleInvite}
                  disabled={inviting || (inviteRole !== 'admin' && !inviteDefaultReviewer)}
                  className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {inviting ? '追加中...' : '追加する'}
                </button>
                <button type="button" onClick={() => setShowInvite(false)} className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-600">キャンセル</button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-3 overflow-hidden rounded-lg bg-white shadow-sm">
          <table className="w-full">
            <thead className="border-b bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs text-gray-500">名前</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500">ロール</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500">最終ログイン日時</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500">アプリ利用</th>
                <th className="px-4 py-2 text-left text-xs text-gray-500">デフォルトフィードバッカー</th>
                <th className="px-4 py-2 text-right text-xs text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    {editingNameUserId === user.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleChangeName(user.id)}
                          className="rounded border border-gray-300 px-2 py-1 text-sm"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => handleChangeName(user.id)}
                          disabled={!editName.trim()}
                          className="rounded bg-blue-600 px-2 py-1 text-xs text-white disabled:opacity-50"
                        >
                          保存
                        </button>
                        <button
                          type="button"
                          onClick={() => { setEditingNameUserId(null); setEditName(''); }}
                          className="text-xs text-gray-500"
                        >
                          取消
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">{user.name}</p>
                        <button
                          type="button"
                          onClick={() => { setEditingNameUserId(user.id); setEditName(user.name); }}
                          className="text-xs text-blue-500 hover:underline"
                        >
                          変更
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-gray-400">{user.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    {editingRoleUserId === user.id ? (
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col gap-1">
                          <select
                            value={editRole}
                            onChange={e => setEditRole(e.target.value as 'coach' | 'feedbacker' | 'admin')}
                            className="rounded border border-gray-300 px-2 py-1 text-sm"
                          >
                            <option value="coach">コーチ</option>
                            <option value="feedbacker">フィードバッカー</option>
                            <option value="admin">管理者</option>
                          </select>
                          {editRole !== 'admin' && (
                            <select
                              value={editRoleDefaultReviewerId}
                              onChange={e => setEditRoleDefaultReviewerId(e.target.value)}
                              className="rounded border border-gray-300 px-2 py-1 text-sm"
                            >
                              <option value="" disabled>デフォルトFBを選択</option>
                              {feedbackers.filter(fb => fb.id !== user.id).map(fb => <option key={fb.id} value={fb.id}>{fb.name}</option>)}
                            </select>
                          )}
                        </div>
                        <button type="button" onClick={() => handleChangeRole(user.id)} className="rounded bg-blue-600 px-2 py-1 text-xs text-white">保存</button>
                        <button type="button" onClick={() => { setEditingRoleUserId(null); setEditRoleDefaultReviewerId(''); }} className="text-xs text-gray-500">取消</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <RoleBadge user={user} />
                        <button
                          type="button"
                          onClick={() => {
                            setEditingRoleUserId(user.id);
                            setEditRole(roleValue(user));
                            setEditRoleDefaultReviewerId(user.default_reviewer_id ?? '');
                          }}
                          className="text-xs text-blue-500 hover:underline"
                        >
                          変更
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <LastLoginCell user={user} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-2 text-xs text-gray-600">
                      {user.is_admin ? (
                        <span className="text-gray-500">常に利用可</span>
                      ) : user.is_feedbacker ? (
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            aria-label={`${user.name} アプリ利用`}
                            checked={user.can_access_learning_review_app}
                            onChange={e => handleChangeLearningFlags(user, { can_access_learning_review_app: e.target.checked })}
                          />
                          <span>許可</span>
                        </label>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          aria-label={`${user.name} ベテラン判定対象`}
                          checked={user.is_learning_review_veteran}
                          onChange={e => handleChangeLearningFlags(user, { is_learning_review_veteran: e.target.checked })}
                        />
                        <span>ベテラン判定対象</span>
                      </label>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {editingUserId === user.id ? (
                      <div className="flex items-center gap-2">
                        <select value={editReviewerId} onChange={e => setEditReviewerId(e.target.value)} className="rounded border border-gray-300 px-2 py-1 text-sm">
                          <option value="" disabled>選択してください</option>
                          {feedbackers.filter(fb => fb.id !== user.id).map(fb => <option key={fb.id} value={fb.id}>{fb.name}</option>)}
                        </select>
                        <button type="button" onClick={() => handleChangeReviewer(user.id)} disabled={!editReviewerId} className="rounded bg-blue-600 px-2 py-1 text-xs text-white disabled:cursor-not-allowed disabled:bg-gray-300">保存</button>
                        <button type="button" onClick={() => setEditingUserId(null)} className="text-xs text-gray-500">取消</button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => { setEditingUserId(user.id); setEditReviewerId(user.default_reviewer_id ?? ''); }}
                        className="text-sm text-gray-600 hover:text-blue-600">
                        {user.default_reviewer_name ?? '—'}
                        <span className="ml-1 text-xs text-blue-500">変更</span>
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => handleDeleteUser(user)}
                      disabled={deletingUserId === user.id || user.id === currentUser?.id}
                      className="rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {deletingUserId === user.id ? '削除中...' : '削除'}
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">ユーザーがいません</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
